import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "../cn";

const badge = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-sans text-meta font-medium",
  {
    variants: {
      // Semantic tone is separate from the accent on purpose: an accent is
      // identity, a severity is information. Conflating them means an alert
      // stops reading as one the moment branding changes.
      tone: {
        neutral: "bg-surface-2 text-ink-2",
        accent: "bg-accent-soft text-accent-text",
        positive: "bg-positive-soft text-positive-text",
        warning: "bg-warning-soft text-warning-text",
        critical: "bg-critical-soft text-critical-text",
        // `info` is a standing fact the reader cannot act on — "Audited",
        // "counted now", "READ-ONLY". It is not a severity and must not be
        // read as one. Added because six screens reached for it and, finding
        // no tone, hand-wrote a span with the same two token classes.
        info: "bg-info-soft text-info-text",
        value: "bg-value-soft text-value-text",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badge> {}

/** A small status label. */
export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badge({ tone }), className)} {...props} />;
}
